import { useTranslation } from 'react-i18next';
import { formatDistanceToNow } from 'date-fns';
import { vi, enUS } from 'date-fns/locale';
import {
  AlertTriangle,
  Bell,
  CheckCircle,
  Edit3,
  Eye,
  FilePlus,
  MessageSquare,
  Pencil,
  Send,
  Tag as TagIcon,
  XCircle,
  type LucideIcon,
} from 'lucide-react';
import type { ClientActivity, ClientActivityType } from '@repo/shared-types';
import i18n from 'i18next';
import { ActivityItem as ActivityRow } from '../../../components/activity/ActivityItem';

const ICONS: Record<ClientActivityType, LucideIcon> = {
  INVOICE_CREATED: FilePlus,
  INVOICE_SENT: Send,
  INVOICE_PAID: CheckCircle,
  INVOICE_OVERDUE: AlertTriangle,
  INVOICE_CANCELLED: XCircle,
  INVOICE_VIEWED: Eye,
  REMINDER_SENT: Bell,
  NOTE_ADDED: MessageSquare,
  NOTE_EDITED: Edit3,
  TAG_ADDED: TagIcon,
  TAG_REMOVED: TagIcon,
  INFO_UPDATED: Pencil,
};

/** Colour is reserved for money (13 §5). INVOICE_CANCELLED's grey went with
 *  that rule — a cancelled invoice is not a money event, it is an absence of
 *  one, and it now reads at the same weight as everything else. */
const ICON_COLORS: Partial<Record<ClientActivityType, string>> = {
  INVOICE_PAID: 'text-brand-500',
  INVOICE_OVERDUE: 'text-destructive',
};

function formatVND(amount: number): string {
  return new Intl.NumberFormat('vi-VN', {
    style: 'currency',
    currency: 'VND',
    maximumFractionDigits: 0,
  }).format(amount);
}

function renderText(activity: ClientActivity, t: ReturnType<typeof useTranslation>['t']): string {
  const m = (activity.metadata ?? {}) as Record<string, unknown>;
  switch (activity.type) {
    case 'INVOICE_CREATED':
      return t('activity.invoiceCreated', { code: m.invoiceCode, amount: formatVND(Number(m.amount ?? 0)) });
    case 'INVOICE_SENT': {
      const channel = (m.channel as string | undefined) ?? 'email';
      return t(channel === 'zalo' ? 'activity.invoiceSentZalo' : 'activity.invoiceSent', {
        code: m.invoiceCode,
      });
    }
    case 'INVOICE_PAID':
      return t('activity.invoicePaid', { code: m.invoiceCode, amount: formatVND(Number(m.amount ?? 0)) });
    case 'INVOICE_OVERDUE':
      return t('activity.invoiceOverdue', { code: m.invoiceCode });
    case 'INVOICE_CANCELLED':
      return t('activity.invoiceCancelled', { code: m.invoiceCode });
    case 'INVOICE_VIEWED':
      return t('activity.invoiceViewed', { code: m.invoiceCode });
    case 'REMINDER_SENT':
      return t('activity.reminderSent', { code: m.invoiceCode });
    case 'NOTE_ADDED':
      return t('activity.noteAdded', { preview: m.contentPreview ?? '' });
    case 'NOTE_EDITED':
      return t('activity.noteEdited');
    case 'TAG_ADDED':
      return t('activity.tagAdded', { tag: m.tag });
    case 'TAG_REMOVED':
      return t('activity.tagRemoved', { tag: m.tag });
    case 'INFO_UPDATED':
      return t('activity.infoUpdated', {
        field: t(`fields.${m.field}`, { defaultValue: m.field }),
        oldValue: m.oldValue ?? '—',
        newValue: m.newValue ?? '—',
      });
    default:
      return activity.type;
  }
}

export function ActivityItem({ activity }: { activity: ClientActivity }) {
  const { t } = useTranslation('clients');
  const Icon = ICONS[activity.type] ?? Pencil;
  const locale = i18n.language === 'vi' ? vi : enUS;
  const text = renderText(activity, t);
  const time = formatDistanceToNow(new Date(activity.createdAt), { locale, addSuffix: true });

  return <ActivityRow icon={Icon} iconClassName={ICON_COLORS[activity.type]} text={text} time={time} />;
}
